import {
  useRef,
  useState,
  useCallback,
  useEffect,
} from 'react';

interface PCMPlayerProps {
  sampleRate?: number;
  channels?: number;
  onStop?: () => void; // Added callback prop
}

export function usePCMPlayer(props: PCMPlayerProps) {
  const { sampleRate = 24000, channels = 1, onStop } = props;
  const audioCtxRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<AudioBufferSourceNode | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const stop = useCallback(() => {
    if (sourceRef.current) {
      try {
        sourceRef.current.stop();
      } catch (e) {
        console.error(e);
      }
      sourceRef.current = null;
    }
    if (audioCtxRef.current) {
      void audioCtxRef.current.close();
      audioCtxRef.current = null;
    }
    setIsPlaying(false);
    onStop?.();
  }, [onStop]);

  const play = useCallback((pcmBuffer: ArrayBuffer) => {
    stop(); // Stop any previous playback
    // Nothing to play (e.g. TTS returned an empty stream) — createBuffer(0 frames) throws.
    if (pcmBuffer.byteLength < 2) {
      return;
    }
    const audioCtx = new window.AudioContext({ sampleRate });
    audioCtxRef.current = audioCtx;
    const pcm16 = new Int16Array(pcmBuffer);
    const float32 = new Float32Array(pcm16.length);
    for (let i = 0; i < pcm16.length; i++) {
      float32[i] = pcm16[i] / 32768;
    }
    const audioBuffer = audioCtx.createBuffer(
      channels,
      float32.length / channels,
      sampleRate,
    );
    audioBuffer.getChannelData(0).set(float32);
    const source = audioCtx.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(audioCtx.destination);
    source.onended = () => {
      setIsPlaying(false);
      void audioCtx.close();
      audioCtxRef.current = null;
      sourceRef.current = null;
      onStop?.();
    };
    sourceRef.current = source;
    source.start();
    setIsPlaying(true);
  }, [channels, sampleRate, stop, onStop]);

  useEffect(() => () => { stop(); }, [stop]);

  return { play, stop, isPlaying };
}
